import { Box, TextField, toast, Typography } from '@montugroup/design-system';
import { styled } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';
import type { NavigateFunction } from 'react-router-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  AuthCardContainer,
  AuthFormGroup,
  AuthPageContainer,
  CardHalfContent,
  CardHalfDecoration,
  MobileLogoContainer,
  MobileOnly,
  StyledLoadingButton,
} from '@/components/auth/authPage';
import settings from '@/constants/constants';
import { Logger } from '@/utils/logger';
import { PapayaContained } from '@montugroup/circuit-shared/components/common/Papaya';
import { PasswordRequirements } from '@montugroup/circuit-shared/components/common/PasswordRequirements';

import { FORGOT_PASSWORD_ROUTE, LOGIN_ROUTE } from '@/routes/route-paths';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserPasswordSchema } from '@montugroup/circuit-api-contracts';
import { ACTOR_TYPE, escriptConformanceId, ShowPasswordAdornment } from '@montugroup/circuit-shared';
import theme from '@montugroup/circuit-shared/theme';
import { alternaleaf, BrandThemeProvider } from '@montugroup/themes';
import { z } from 'zod';
import logo from '../../components/layout/alternaleaf.svg';

const logger = new Logger('ResetPassword');
const { url } = settings;

const DUMMY_EMAIL = 'example@montu.com.au';

const AuthCardBackground = styled(Box)(({ theme }) => ({
  height: '100%',
  width: '100%',
  backgroundColor: theme.palette.primary.main,
  // eslint-disable-next-line quotes
  backgroundImage: "url('/assets/images/patientBack_AL.png')",
  backgroundPosition: 'top left',
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-start',
  padding: '2rem',
  borderTopLeftRadius: '25px',
  borderBottomLeftRadius: '25px',
}));

export interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

enum PasswordErrorCode {
  PASSWORD_REUSE = 'PasswordReuseError',
  CURRENT_PASSWORD = 'CurrentPasswordError',
  REQUIREMENTS_NOT_MET = 'password.requirements_not_met',
}

const formSchema = z
  .object({
    password: UserPasswordSchema,
    confirmPassword: z.string().min(1, { message: 'Please confirm your password' }),
  })
  .superRefine((values, ctx) => {
    if (values.confirmPassword !== values.password) {
      ctx.addIssue({
        message: 'Passwords do not match',
        code: 'custom',
        path: ['confirmPassword'],
      });
    }
  });

const validateExpiryEpoch = (epochDate: number, navigate: NavigateFunction): void => {
  const now = new Date().toISOString();
  const nowEpoch = Date.parse(now);
  if (nowEpoch > epochDate) {
    toast.error(
      `Sorry, your password reset link has expired or is invalid. Please try again or contact our support on ${settings.support.phone.display}`,
      { autoClose: false },
    );
    navigate(FORGOT_PASSWORD_ROUTE);
  }
};

export function ResetPasswordPage() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<boolean>(false);
  const [isPasswordRevealed, setIsPasswordRevealed] = useState<boolean>(false);
  const [isConfirmPasswordRevealed, setIsConfirmPasswordRevealed] = useState<boolean>(false);

  const getToken = useCallback(() => {
    const hashParams = new URLSearchParams(location.hash.slice(1));

    return hashParams.get('token') || params?.id || '';
  }, [location.hash, params?.id]);

  const { register, handleSubmit, formState, control } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const password = useWatch({ name: 'password', control });
  const shouldShowForm = !message || password === '';
  const { errors } = formState;

  const onSubmit: SubmitHandler<ResetPasswordFormData> = async (formData: ResetPasswordFormData) => {
    setLoading(true);

    try {
      // TODO: Refactor to use ts-rest api client or at least axios api client if ts-rest is difficult
      const res = await fetch(`${url}/user/passwordupdate/${getToken()}`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Conformance-Id': escriptConformanceId,
        },
        body: JSON.stringify({
          // NOTE: An email is required here because we need to send an email key on the body
          email: DUMMY_EMAIL,
          password: formData.password,
        }),
      });

      const result = await res.json();

      if (res.status === 400) {
        switch (result.error_code) {
          case PasswordErrorCode.PASSWORD_REUSE:
            toast.error('This password has been used before. Please choose a different password.');
            break;
          case PasswordErrorCode.CURRENT_PASSWORD:
            toast.error('This is your current password. Please choose a different password.');
            break;
          default:
            toast.error(result.message || result.error.message || 'Invalid password. Please try again.');
        }

        setLoading(false);
        return;
      }

      if (res.status === 403) {
        switch (result.error.code) {
          case PasswordErrorCode.REQUIREMENTS_NOT_MET:
            toast.error(
              'The password you entered is invalid as it does not meet our security requirements. Please choose a different password.',
            );
            break;
          default:
            toast.error(result.message || result.error.message || 'Invalid password. Please try again.');
        }

        setLoading(false);
        return;
      }

      if (res.status !== 200) {
        throw Error(result.message);
      }
      setMessage((msg) => !msg);

      if (result.user.role_id === ACTOR_TYPE.patient) {
        toast.success('Password changed successfully');
        navigate(LOGIN_ROUTE);
        return;
      }

      toast.success('Password changed successfully');
      navigate('/');
    } catch (err) {
      toast.error(`Something went wrong. Please try again or contact support on ${settings.support.phone.display}`);
      setLoading(false);
      logger.error(err);
    }
  };

  useEffect(() => {
    const token = getToken().split('$');
    if (token?.length === 3) {
      const epochExpiry = Number(token[2]);

      validateExpiryEpoch(epochExpiry, navigate);
    }
  }, [getToken, navigate, params.id]);

  return (
    <div className="w-100 h-100">
      <BrandThemeProvider theme={alternaleaf}>
        <AuthPageContainer>
          <AuthCardContainer>
            <CardHalfDecoration>
              <AuthCardBackground>
                <PapayaContained />
              </AuthCardBackground>
            </CardHalfDecoration>
            <CardHalfContent>
              <MobileLogoContainer>
                <a href={settings.marketing.site.url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={logo}
                    alt="Alternaleaf"
                    style={{ padding: theme.spacing(4), paddingTop: theme.spacing(2) }}
                  />
                </a>
              </MobileLogoContainer>
              <div>
                {shouldShowForm && (
                  <form style={{ padding: theme.spacing(4) }} data-private method="post">
                    <Typography variant="h2" sx={(theme) => ({ marginBottom: theme.spacing(4) })}>
                      Reset Password
                    </Typography>
                    <Typography sx={(theme) => ({ marginBottom: theme.spacing(4) })} data-testid="subtitle">
                      Please enter your new password below. Your password must:
                      <PasswordRequirements />
                    </Typography>

                    <AuthFormGroup>
                      <TextField
                        type={isPasswordRevealed ? 'text' : 'password'}
                        id="userPassword"
                        {...register('password')}
                        error={Boolean(errors.password)}
                        helperText={errors.password?.message ?? ''}
                        placeholder="New Password"
                        data-testid="new-password-form-input"
                        fullWidth
                        sx={{ marginBottom: 4 }}
                        InputProps={{
                          endAdornment: (
                            <ShowPasswordAdornment isRevealed={isPasswordRevealed} toggle={setIsPasswordRevealed} />
                          ),
                        }}
                      />
                      <TextField
                        type={isConfirmPasswordRevealed ? 'text' : 'password'}
                        id="userConfirmPassword"
                        {...register('confirmPassword')}
                        error={Boolean(errors.confirmPassword)}
                        helperText={errors.confirmPassword?.message ?? ''}
                        placeholder="Confirm Password"
                        fullWidth
                        sx={{ marginBottom: 4 }}
                        data-testid="confirm-password-form-input"
                        InputProps={{
                          endAdornment: (
                            <ShowPasswordAdornment
                              isRevealed={isConfirmPasswordRevealed}
                              toggle={setIsConfirmPasswordRevealed}
                            />
                          ),
                        }}
                      />
                    </AuthFormGroup>
                    <StyledLoadingButton
                      id="btnSubmit"
                      loading={loading}
                      variant="contained"
                      color="primary"
                      className="w-100"
                      type="submit"
                      onClick={handleSubmit(onSubmit)}
                      data-testid="submit-button"
                    >
                      Reset
                    </StyledLoadingButton>
                  </form>
                )}
              </div>
            </CardHalfContent>
          </AuthCardContainer>
        </AuthPageContainer>
      </BrandThemeProvider>
      <MobileOnly>
        <PapayaContained sx={{ position: 'fixed' }} />
      </MobileOnly>
    </div>
  );
}

export default ResetPasswordPage;
